import { isGroup } from 'tabview/blocks/viewModel$/util';
import { includeAncestors } from 'tabview/graphViews/util';
import type {
  BlocksViewGraph,
  BlocksViewGroup,
  BlocksViewLink,
  BlocksViewNode,
} from 'tabview/blocks/types';

const some = <T>(items: Iterable<T>, predicate: (item: T) => boolean) => {
  for (const item of items) {
    if (predicate(item)) {
      return true;
    }
  }
  return false;
};

const isNodeUnderRoot = (node: BlocksViewNode, root: BlocksViewNode) =>
  node === root || some(includeAncestors(node), ancestor => ancestor === root);

const isLinkUnderRoot = (root: BlocksViewNode, link: BlocksViewLink) =>
  isNodeUnderRoot(link.source, root) && isNodeUnderRoot(link.target, root);

/** @returns the links of all descendants of {@link node}. */
function* descendantLinks(node: BlocksViewNode): Iterable<BlocksViewLink> {
  if (!node.children) {
    return;
  }
  for (const child of node.children) {
    if (child.links) {
      yield* child.links;
    }
    if (isGroup(child)) {
      yield* descendantLinks(child);
    }
  }
}
/** @returns a deep clone of {@link original}, clearing positioning data and assigning {@link BlocksViewNode.children|children} and {@link BlocksViewNode.parent|parent} from {@link nodeMap}. */
const cloneNode = (
  original: BlocksViewNode,
  nodeMap: Map<BlocksViewNode, BlocksViewNode>
): BlocksViewNode => {
  if (nodeMap.has(original)) {
    return nodeMap.get(original)!;
  }

  const cloned: BlocksViewNode = {
    ...original,
    parent: null,
    children: null,
    cell: undefined,
    bounds: [0, 0, 1, 1],
    content: undefined,
  };
  nodeMap.set(original, cloned);

  if (isGroup(original)) {
    cloned.children = original.children.map(child => {
      const clonedChild = cloneNode(child, nodeMap);
      clonedChild.parent = cloned;
      return clonedChild;
    });
  }

  return cloned;
};

const cloneEdge = (
  link: BlocksViewLink,
  nodeMap: Map<BlocksViewNode, BlocksViewNode>
): BlocksViewLink | null => {
  const source = nodeMap.get(link.source);
  const target = nodeMap.get(link.target);
  if (!source || !target) {
    return null;
  }
  return { ...link, source, target, route: undefined };
};

/**
 * @returns an {@link Array} containing clones of all links which are contained under {@link root}.
 *
 * links whose {@link BlocksViewLink.source|source} or {@link BlocksViewLink.target|target} are outside {@link root} will be excluded.
 */
const cloneEdges = (
  root: BlocksViewNode,
  nodeMap: Map<BlocksViewNode, BlocksViewNode>
) => {
  const edges: BlocksViewLink[] = [];
  for (const link of descendantLinks(root)) {
    const clonedLink = cloneEdge(link, nodeMap);
    if (!clonedLink || !isLinkUnderRoot(root, clonedLink)) {
      continue;
    }
    edges.push(clonedLink);
  }
  return edges;
};

/**
 * @returns a {@link BlocksViewGraph} with a deep clone of {@link node} as the {@link BlocksViewGraph.root} and clones of all contained descendant links as the {@link BlocksViewGraph.edges}.
 *
 * Positioning data like {@link BlocksViewNode.cell} and {@link BlocksViewLink.route} will be cleared.
 */
const cloneSubGraph = (node: BlocksViewGroup): BlocksViewGraph => {
  /** a {@link Map} of the cloned nodes, keyed by the original nodes. this is necessary so the {@link BlocksViewNode.children|children}, {@link BlocksViewNode.parent|parent} properties can be updated to the clones. */
  const nodeMap = new Map<BlocksViewNode, BlocksViewNode>();
  const root = cloneNode({ ...node, open: true, parent: null }, nodeMap);
  const edges = cloneEdges(root, nodeMap);

  return {
    root,
    edges,
  };
};

export { cloneSubGraph };
